// src/components/sections/Hero3Contact.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { motion } from "framer-motion";

import imagen5 from "../images/imagen5.webp";

const MotionBox = motion(Box);

export default function Hero3Contact({
  title = "Si necesitás ayuda, estamos para tí.",
  subtitle = "Escribinos por el canal que te quede más cómodo y te respondemos lo antes posible.",
  email = "lacasahostal.bichon@gmail.com",
  instagramUrl = "https://www.instagram.com/hostalbichon.chile/",
  facebookUrl = "https://www.facebook.com/profile.php?id=100064051594523",
  whatsappNumber = "56991312384",
  // 👇 agrego URL para reserva (poné tu ruta / link real)
  bookingUrl = "/reserve",
}) {
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    "Hola! Vengo de la web, quiero hacer una consulta 🙂",
  )}`;

  // Colores pedidos
  const COLORS = {
    mail: "#20AB8A",
    ig: "#c72e64",
    fb: "#27276d",
    wa: "#16a357",
  };

  return (
    <Box
      component="section"
      id="contactanos"
      sx={{
        position: "relative",
        overflow: "hidden",
        scrollMarginTop: { xs: "84px", md: "96px" },
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${imagen5})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.03)",
        }}
      />

      {/* Dark overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.58)",
        }}
      />

      <Container
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 8, sm: 10, md: 12 },
          minHeight: { xs: "auto", md: "70vh" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 3, md: 3.5 },
        }}
      >
        {/* Title block */}
        <MotionBox
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.45 }}
          sx={{ textAlign: "center", maxWidth: 820 }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "common.white",
              fontWeight: 400,
              letterSpacing: -0.5,
              fontFamily: '"DynaPuff", cursive',
              fontSize: { xs: "2rem", sm: "2.4rem", md: "2.8rem" },
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              mt: 1.2,
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: "1rem", sm: "1.05rem" },
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>
        </MotionBox>

        {/* CTA Reserva (para no cortar el funnel) */}
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.45, delay: 0.03 }}
          sx={{ width: "100%", maxWidth: 520 }}
        >
          <Button
            fullWidth
            size="large"
            startIcon={<CalendarMonthRoundedIcon />}
            href={bookingUrl}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 900,
              py: 1.4,
              bgcolor: "rgba(255,255,255,0.92)",
              color: "rgba(0,0,0,0.86)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
          >
            Reservar ahora
          </Button>
        </MotionBox>

        {/* Contact card */}
        <MotionBox
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          sx={{ width: "100%", maxWidth: 920 }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              backdropFilter: "blur(10px)",
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "common.white",
            }}
          >
            <CardContent sx={{ p: { xs: 2.2, sm: 3 } }}>
              <Grid container spacing={2}>
                {/* Email */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(0,0,0,0.12)",
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <IconButton
                        size="small"
                        sx={{
                          color: "common.white",
                          bgcolor: "rgba(255,255,255,0.10)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
                        }}
                      >
                        <EmailRoundedIcon />
                      </IconButton>
                      <Box>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                          Email
                        </Typography>
                        <Typography sx={{ opacity: 0.9, fontSize: 14 }}>
                          {email}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider
                      sx={{
                        my: 1.6,
                        borderColor: "rgba(255,255,255,0.14)",
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      href={`mailto:${email}`}
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 900,
                        bgcolor: COLORS.mail,
                        color: "#fff",
                        "& .MuiButton-startIcon": { color: "#fff" },
                        "&:hover": {
                          bgcolor: COLORS.mail,
                          filter: "brightness(1.08)",
                        },
                      }}
                    >
                      Enviar correo
                    </Button>
                  </Box>
                </Grid>

                {/* Socials + WhatsApp */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(0,0,0,0.12)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, mb: 1 }}>
                      Redes y WhatsApp
                    </Typography>

                    <Grid container spacing={1.2}>
                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<InstagramIcon />}
                          href={instagramUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 900,
                            bgcolor: COLORS.ig,
                            color: "#fff",
                            "& .MuiButton-startIcon": { color: "#fff" },
                            "&:hover": {
                              bgcolor: COLORS.ig,
                              filter: "brightness(1.08)",
                            },
                          }}
                        >
                          Instagram
                        </Button>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<FacebookRoundedIcon />}
                          href={facebookUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 900,
                            bgcolor: COLORS.fb,
                            color: "#fff",
                            "& .MuiButton-startIcon": { color: "#fff" },
                            "&:hover": {
                              bgcolor: COLORS.fb,
                              filter: "brightness(1.08)",
                            },
                          }}
                        >
                          Facebook
                        </Button>
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          href={whatsappUrl}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            borderRadius: 3,
                            textTransform: "none",
                            fontWeight: 900,
                            bgcolor: COLORS.wa,
                            color: "#fff",
                            "& .MuiButton-startIcon": { color: "#fff" },
                            "&:hover": {
                              bgcolor: COLORS.wa,
                              filter: "brightness(1.08)",
                            },
                          }}
                        >
                          Escribir por WhatsApp
                        </Button>

                        <Typography
                          sx={{
                            mt: 1,
                            fontSize: 12.5,
                            opacity: 0.85,
                            textAlign: "center",
                          }}
                        >
                          Respuesta rápida para reservas y consultas.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </MotionBox>
      </Container>
    </Box>
  );
}
